import { Box, Flex, Heading, Link, Text } from '@chakra-ui/react';
import {
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import {
  date as commonDate,
  typeguards as commonTypeguards,
} from '@finley-technologies/common';
import { DataLake } from '@finley-technologies/finley-types';

import {
  CollateralDataQualityCheckBreadcrumbs,
  CollateralDataQualityCheckResultBadge,
} from './CollateralDataQualityCheckComponents';
import {
  CollateralDataQualityViewMode,
  currentReportingDateColumnName,
  currentValueColumnName,
  finleyreceivableidColumnName,
  previousReportingDateColumnName,
  previousValueColumnName,
} from './utils';
import { colors } from '~/packages/common-frontend/colors';
import { BasicTable } from '~/packages/common-frontend/components/TanStack';

type CollateralDataQualityCheckDetailsProps = {
  queryResults: DataLake.AthenaQueryItemData[];
  configName: string;
  setViewMode: React.Dispatch<
    React.SetStateAction<CollateralDataQualityViewMode>
  >;
  receivablesRoutePrefix: string;
};
export const CollateralDataQualityCheckDetails = ({
  queryResults,
  configName,
  setViewMode,
  receivablesRoutePrefix,
}: CollateralDataQualityCheckDetailsProps) => {
  const [sorting, setSorting] = useState<SortingState>([]);

  const table = useReactTable({
    data: queryResults,
    columns: [
      {
        id: finleyreceivableidColumnName,
        accessorKey: finleyreceivableidColumnName,
        header: function formatHeader() {
          return (
            <Text color="black" fontSize="16px" py="8px">
              Receivable ID
            </Text>
          );
        },
        cell: function formatCell({ getValue }) {
          const value = getValue();
          return (
            <Link
              color={colors.info}
              as={RouterLink}
              to={`${receivablesRoutePrefix}/${value}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {value != null ? value.toString() : '-'}
            </Link>
          );
        },
      },
      {
        id: previousReportingDateColumnName,
        accessorKey: previousReportingDateColumnName,
        header: function formatHeader() {
          return (
            <Text color="black" fontSize="16px" py="8px">
              Previous Reporting Date
            </Text>
          );
        },
        cell: function formatCell({ getValue }) {
          const value = getValue();
          return (
            <Text color={colors.black[70]} py="8px">
              {commonTypeguards.isFullDateStr(value)
                ? commonDate.fullDateStrToHumanDateString(value)
                : '-'}
            </Text>
          );
        },
      },
      {
        id: previousValueColumnName,
        accessorKey: previousValueColumnName,
        header: function formatHeader() {
          return (
            <Text color="black" fontSize="16px" py="8px">
              Previous Value
            </Text>
          );
        },
        cell: function formatCell({ getValue }) {
          const value = getValue();
          return (
            <Text color={colors.black[70]} py="8px">
              {value != null ? value.toString() : '-'}
            </Text>
          );
        },
      },
      {
        id: currentReportingDateColumnName,
        accessorKey: currentReportingDateColumnName,
        header: function formatHeader() {
          return (
            <Text color="black" fontSize="16px" py="8px">
              Current Reporting Date
            </Text>
          );
        },
        cell: function formatCell({ getValue }) {
          const value = getValue();
          return (
            <Text color={colors.black[70]} py="8px">
              {commonTypeguards.isFullDateStr(value)
                ? commonDate.fullDateStrToHumanDateString(value)
                : '-'}
            </Text>
          );
        },
      },
      {
        id: currentValueColumnName,
        accessorKey: currentValueColumnName,
        header: function formatHeader() {
          return (
            <Text color="black" fontSize="16px" py="8px">
              Current Value
            </Text>
          );
        },
        cell: function formatCell({ getValue }) {
          const value = getValue();
          return (
            <Text color={colors.black[70]} py="8px">
              {value != null ? value.toString() : '-'}
            </Text>
          );
        },
      },
    ],
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });
  return (
    <Box>
      <CollateralDataQualityCheckBreadcrumbs
        setViewMode={setViewMode}
        cdqConfigName={configName}
      />
      <Flex alignItems="center" mt="40px" mb="40px">
        <Heading mr="24px">{configName}</Heading>
        <CollateralDataQualityCheckResultBadge
          numberOfFailures={queryResults.length}
          badgeSize="large"
        />
      </Flex>
      <BasicTable table={table} maxHeight="65vh" />
    </Box>
  );
};
